import {useEffect, useState} from 'react';
import {Grid, MenuItem} from '@mui/material';
import {ExpansionPanel, InputField, MoneyField} from '@/components/common';
import {useCompanyAnnualStore} from '@/store/audit/companyAnnualStore';
import type {CompanyAnnual} from '@/services/accountService/companyAnnualService';

type Props = {
    annualId: number;
    onChange?: (patch: Partial<CompanyAnnual>) => void;
    showLastYearTwoTierTax?: boolean;
    disableAccordion?: boolean;
    hideAssessableAndTaxLossFields?: boolean;
    initialData?: {
        twoTierTaxThisYear?: number;
    } | null;
};

export default function EpTax({annualId, onChange, showLastYearTwoTierTax = true, disableAccordion = false, hideAssessableAndTaxLossFields = false, initialData}: Props) {
    const store = useCompanyAnnualStore();
    const [local, setLocal] = useState<{
        twoTierTaxThisYear?: number;
        twoTierTaxLastYear?: number;
        assessableAmount?: number;
        taxLossBF?: number;
        taxLossCF?: number;
    }>({});

    useEffect(() => {
        if (initialData) {
            setLocal(prev => ({
                ...prev,
                twoTierTaxThisYear: initialData.twoTierTaxThisYear ?? prev.twoTierTaxThisYear,
            }));
            return;
        }

        let mounted = true;
        (async () => {
            const v = await store.getTaxInfo(annualId);
            if (!mounted) return;
            setLocal(v);
        })();
        return () => {
            mounted = false;
        };
    }, [annualId, store, initialData]);

    const emit = (patch: Partial<typeof local>) => {
        const next = {...local, ...patch};
        setLocal(next);
        onChange?.(patch as Partial<CompanyAnnual>);
    };

    return (
        <ExpansionPanel 
            title="Tax (稅務)"
            expanded={disableAccordion ? true : undefined}
            onChange={disableAccordion ? () => {} : undefined}
        >
            <Grid container spacing={0.5}>
                <Grid size={6 as any}>
                    <InputField select fullWidth variant="outlined" label="This year two tired tax? (今年有否二級稅率)"
                                value={local.twoTierTaxThisYear ?? ''}
                                onChange={(e) => emit({twoTierTaxThisYear: Number(e.target.value) as any})}>
                        <MenuItem dense value="">Select</MenuItem>
                        <MenuItem dense value={0}>No</MenuItem>
                        <MenuItem dense value={1}>Yes</MenuItem>
                    </InputField>
                </Grid>
                {showLastYearTwoTierTax && (
                    <Grid size={6 as any}>
                        <InputField select fullWidth variant="outlined"
                                    label="Last Year two tired tax? (上年有否二級稅率)"
                                    value={local.twoTierTaxLastYear ?? ''}
                                    onChange={(e) => emit({twoTierTaxLastYear: Number(e.target.value) as any})}>
                            <MenuItem dense value="">Select</MenuItem>
                            <MenuItem dense value={0}>No</MenuItem>
                            <MenuItem dense value={1}>Yes</MenuItem>
                        </InputField>
                    </Grid>
                )}
                {!hideAssessableAndTaxLossFields && (
                    <Grid size={6}>
                        {(() => {
                            const amt = Number(local.assessableAmount ?? 0) || 0;
                            const label = amt < 0
                                ? 'Assessable loss (應評稅虧損)'
                                : 'Assessable profit/adjusted (應評稅利潤)';
                            return (
                                <MoneyField
                                    label={label}
                                    value={local.assessableAmount ?? 0}
                                    showCents
                                    editable
                                    align="left"
                                    sx={{width: '100%'}}
                                    onChangeNumber={(n) => emit({assessableAmount: Number(n || 0)})}
                                />
                            );
                        })()}
                    </Grid>
                )}
                {showLastYearTwoTierTax && <Grid size={6}/>} 
                {!hideAssessableAndTaxLossFields && (
                    <Grid size={6 as any}>
                        <InputField fullWidth type="money" allowNegative variant="outlined"
                                    label="Tax loss B/F (稅務虧損 B/F)"
                                    value={(local.taxLossBF == null ? '' : `-${Math.abs(local.taxLossBF)}`)}
                                    onChange={(e) => {
                                        const raw = String(e.target.value ?? '');
                                        const val = raw.replace(/,/g, '');
                                        const normalized = val.startsWith('-') ? val : (val ? `-${val}` : '');
                                        emit({taxLossBF: normalized as any});
                                    }} onBlur={(e) => {
                            const raw = String(e.target.value ?? '').replace(/,/g, '').trim();
                            const n = raw === '' || isNaN(Number(raw)) ? 0 : Number(raw);
                            const neg = -Math.abs(n);
                            emit({taxLossBF: neg as any});
                        }}/>
                    </Grid>
                )}
                {!hideAssessableAndTaxLossFields && (
                    <Grid size={6 as any}>
                        <InputField fullWidth type="money" allowNegative variant="outlined"
                                    label="Tax loss C/F (稅務虧損 C/F)"
                                    value={(local.taxLossCF == null ? '' : `-${Math.abs(local.taxLossCF)}`)}
                                    onChange={(e) => {
                                        const raw = String(e.target.value ?? '');
                                        const val = raw.replace(/,/g, '');
                                        const normalized = val.startsWith('-') ? val : (val ? `-${val}` : '');
                                        emit({taxLossCF: normalized as any});
                                    }} onBlur={(e) => {
                            const raw = String(e.target.value ?? '').replace(/,/g, '').trim();
                            const n = raw === '' || isNaN(Number(raw)) ? 0 : Number(raw);
                            const neg = -Math.abs(n);
                            emit({taxLossCF: neg as any});
                        }}/>
                    </Grid>
                )}
            </Grid>
        </ExpansionPanel>
    );
}
